import type { ApiSchemas } from '@rental/contracts/client'
import { formatDateTime } from '@rental/i18n'
import { cn } from '@rental/ui'
import { useRef, useState } from 'react'
import type { CanDo } from '../booking-can'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { ROW_STILL } from '../components/Results'
import { Section } from '../components/Section'
import { useLocale } from '../i18n'
import { type AskedTrip, TripDialog } from './TripDialog'
import { hasTripFacts, TripChips, TripEditButton, TripFacts, useTripWords } from './TripParts'

type Detail = ApiSchemas['StaffBookingDetail']

/**
 * S9 (LOC-5, RES-6): a booking's trips, for each end that is a delivery zone:
 * when each is due, who drives it, the ferry it meets, where the customer is
 * met and the office's note. Whoever gives the trips out writes them here, as
 * on Today. A booking with no trip has no section.
 */
export function BookingTrips({ detail, may }: { detail: Detail; may: CanDo }) {
  const { locale, messages: m } = useLocale()
  const tripWords = useTripWords()
  const dialog = useRef<HTMLDialogElement>(null)
  const [asked, setAsked] = useState<AskedTrip | null>(null)
  const [said, setSaid] = useState<number | null>(null)
  const { booking, deliveries } = detail
  if (deliveries.length === 0) return null
  const writes = may('trips')
  return (
    <Section title={m.trips.title}>
      {said !== null ? (
        <OutcomeNotice key={said}>
          <p>{m.trips.form.saved}</p>
        </OutcomeNotice>
      ) : null}
      <ul className="flex flex-col">
        {deliveries.map((trip) => (
          <li key={trip.leg} className={cn(ROW_STILL, trip.state === 'done' && 'text-text-muted')}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="font-medium">{tripWords(trip.leg, booking)}</span>
              <span className="figures">{formatDateTime(locale, trip.dueAt)}</span>
              <div className="ms-auto flex flex-wrap gap-2">
                <TripChips trip={trip} />
              </div>
            </div>
            {hasTripFacts(trip, booking) ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-muted">
                <TripFacts trip={trip} booking={booking} />
              </div>
            ) : null}
            {writes && trip.state !== 'done' ? (
              <TripEditButton
                trip={trip}
                booking={booking}
                onEdit={() => setAsked({ trip, booking })}
              />
            ) : null}
          </li>
        ))}
      </ul>
      {writes ? (
        <TripDialog dialogRef={dialog} asked={asked} onSaved={() => setSaid(Date.now())} />
      ) : null}
    </Section>
  )
}
